import type { FAQItem } from '../components/FAQSection';

// --- Types ---

export type FeatureRating = 'full' | 'partial' | 'none';

export interface FeatureRow {
  name: string;
  category: string;
  pollenate: FeatureRating;
  competitor: FeatureRating;
  note?: string;
}

export interface PricingPlan {
  name: string;
  price: string;
  period?: string;
  features: string[];
  highlighted?: boolean;
}

export interface CompetitorPricing {
  model: string;
  hasFreeTier: boolean;
  plans: PricingPlan[];
}

export interface CompetitorData {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  logoEmoji: string;
  category: string;
  website: string;
  pricingUrl: string;
  yearFounded: number;
  targetAudience: string;
  pricing: CompetitorPricing;
  features: FeatureRow[];
  prosVsPollenate: string[];
  consVsPollenate: string[];
  switchReasons: string[];
  faqs: FAQItem[];
  metaTitle: string;
  metaDescription: string;
}

// --- Pollenate pricing (shared across all comparison pages) ---

export const pollenatePricing: CompetitorPricing = {
  model: 'Per-organization, flat monthly fee',
  hasFreeTier: true,
  plans: [
    {
      name: 'Starter',
      price: '$0',
      period: 'forever',
      features: ['1 inbox', '5,000 events/month', '1 brand', 'Unlimited team members', 'All widget types', 'API access'],
    },
    {
      name: 'Team',
      price: '$49',
      period: '/month',
      features: ['5 inboxes', '100,000 events/month', '5 brands', 'Unlimited team members', 'AI insights', 'Priority support'],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      features: ['Unlimited inboxes', 'Unlimited events', 'Unlimited brands', 'SSO / SAML', 'BAA / HIPAA', 'Dedicated support & SLA'],
    },
  ],
};

// --- Shared feature categories ---

const COLLECTION = 'Collection';
const ANALYTICS = 'Analytics';
const INTEGRATION = 'Integration';
const DEVELOPER = 'Developer Experience';
const PRICING_CAT = 'Pricing & Plans';
const COLLABORATION = 'Collaboration';

// --- Competitor data ---

const qualtrics: CompetitorData = {
  slug: 'qualtrics',
  name: 'Qualtrics',
  tagline: 'Enterprise experience management platform with opaque pricing and complex setup.',
  description: 'Qualtrics XM is a large-scale enterprise experience management suite covering customer, employee, product, and brand experiences. It is powerful but comes with high costs, lengthy onboarding, and a steep learning curve.',
  logoEmoji: '🏢',
  category: 'Enterprise Experience Management',
  website: 'qualtrics.com',
  pricingUrl: 'https://www.qualtrics.com/pricing/',
  yearFounded: 2002,
  targetAudience: 'Large enterprises and research teams',
  pricing: {
    model: 'Custom quotes only, per-interaction pricing',
    hasFreeTier: false,
    plans: [
      {
        name: 'CX Suite',
        price: 'Custom',
        features: ['Voice of Customer', 'Frontline Digital', 'Frontline Care', 'Frontline Locations'],
      },
      {
        name: 'EX Suite',
        price: 'Custom',
        features: ['Employee Engagement', '360 Feedback', 'Lifecycle Surveys', 'Pulse Surveys'],
      },
      {
        name: 'Research Suite',
        price: 'Custom',
        features: ['Survey Design', 'Data Analysis', 'Panel Management', 'Conjoint Analysis'],
      },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'partial', note: 'Qualtrics requires custom intercepts' },
    { name: 'Multi-question feedback pages', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'NPS / CSAT / CES surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Emoji & thumbs reactions', category: COLLECTION, pollenate: 'full', competitor: 'none' },
    { name: 'QR code sharing', category: COLLECTION, pollenate: 'full', competitor: 'partial' },
    { name: 'Real-time analytics dashboard', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'AI-powered insights', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'Sentiment analysis', category: ANALYTICS, pollenate: 'partial', competitor: 'full' },
    { name: 'Cross-tab analysis', category: ANALYTICS, pollenate: 'none', competitor: 'full' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'React / Web Component SDK', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Single script tag embed', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Zapier integration', category: INTEGRATION, pollenate: 'full', competitor: 'full' },
    { name: 'Slack / Teams notifications', category: INTEGRATION, pollenate: 'full', competitor: 'full' },
    { name: 'Salesforce integration', category: INTEGRATION, pollenate: 'partial', competitor: 'full', note: 'Pollenate connects via Zapier; Qualtrics has native integration' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'none' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'none' },
    { name: 'No per-user pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'none', note: 'Pollenate charges per org, not per seat' },
  ],
  prosVsPollenate: [
    'Deep enterprise research capabilities with conjoint analysis and MaxDiff',
    'Comprehensive employee experience and 360-degree feedback suites',
    'Built-in panel management for audience recruitment',
    'Established enterprise compliance certifications (FedRAMP, ISO 27001)',
  ],
  consVsPollenate: [
    'Transparent, public pricing — no sales calls or custom quotes needed',
    'Get started in minutes with a simple setup and intuitive UI',
    'Drop-in embeddable widgets with a single script tag — no custom intercepts',
    'Full developer toolkit: REST API, React SDK, MCP server, and more',
    'Predictable per-organization pricing with no per-interaction surprises',
    'Right-sized for teams that need lightweight, focused feedback collection',
  ],
  switchReasons: [
    'Get started in minutes with a free tier — no sales calls required',
    'Embed feedback widgets anywhere with a single script tag',
    'Transparent, predictable pricing with unlimited team members',
    'Developer-first approach with REST API, React SDK, and MCP server',
    'AI-powered insights without enterprise-level complexity',
    'Modern stack built on Cloudflare edge — sub-50ms response times globally',
  ],
  faqs: [
    { question: 'Is Pollenate a full replacement for Qualtrics?', answer: 'Pollenate is ideal for product feedback collection, user surveys, and real-time analytics. If you need advanced research tools like conjoint analysis or employee 360-degree reviews, Qualtrics may still be the right fit. But for feedback widgets, embeddable surveys, and developer-friendly integrations, Pollenate offers a much simpler and more affordable solution.' },
    { question: 'Can I migrate my surveys from Qualtrics to Pollenate?', answer: 'Pollenate supports multi-question feedback pages with various question types. While there\'s no automated import from Qualtrics, you can recreate surveys quickly using our drag-and-drop editor or AI-powered form generation.' },
    { question: 'Does Pollenate offer enterprise compliance features?', answer: 'Yes. Our Enterprise plan includes SSO/SAML, BAA for HIPAA compliance, dedicated support, and SLAs. We also support 2FA enforcement at the organization level.' },
    { question: 'How does Pollenate pricing compare to Qualtrics?', answer: 'Qualtrics uses custom per-interaction pricing that can cost thousands per year. Pollenate starts free with 5,000 events/month and scales to $49/month for 100,000 events. Enterprise plans are custom but designed to be transparent and predictable.' },
  ],
  metaTitle: 'Pollenate vs Qualtrics (2026) — Pricing, Features & Comparison',
  metaDescription: 'Compare Pollenate and Qualtrics for feedback collection. See pricing, features, and why teams switch from Qualtrics to Pollenate for simpler, more affordable user feedback.',
};

const googleForms: CompetitorData = {
  slug: 'google-forms',
  name: 'Google Forms',
  tagline: 'Free basic survey tool with no analytics, embeddable widgets, or developer API.',
  description: 'Google Forms is a free form builder included with Google Workspace. It\'s great for quick internal surveys but lacks feedback-specific features like embeddable widgets, real-time analytics, branding controls, and developer APIs.',
  logoEmoji: '📝',
  category: 'Free Survey Tool',
  website: 'forms.google.com',
  pricingUrl: 'https://www.google.com/forms/about/',
  yearFounded: 2008,
  targetAudience: 'Anyone with a Google account',
  pricing: {
    model: 'Free with Google account, or part of Google Workspace',
    hasFreeTier: true,
    plans: [
      {
        name: 'Free',
        price: '$0',
        features: ['Unlimited forms', 'Unlimited responses', 'Basic question types', 'Google Sheets integration'],
      },
      {
        name: 'Business Starter',
        price: '$7',
        period: '/user/month',
        features: ['Custom business email', '30 GB storage/user', 'Forms included', 'Basic admin controls'],
      },
      {
        name: 'Business Standard',
        price: '$14',
        period: '/user/month',
        features: ['2 TB storage/user', 'Meeting recordings', 'eSignature', 'Forms included'],
        highlighted: true,
      },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'none', note: 'Google Forms has no widget — only iframe embeds' },
    { name: 'Multi-question surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'NPS / CSAT surveys', category: COLLECTION, pollenate: 'full', competitor: 'none', note: 'Forms lacks dedicated NPS/CSAT question types' },
    { name: 'Emoji & thumbs reactions', category: COLLECTION, pollenate: 'full', competitor: 'none' },
    { name: 'Star ratings', category: COLLECTION, pollenate: 'full', competitor: 'partial', note: 'Forms has linear scale but not star UI' },
    { name: 'Custom branding', category: COLLECTION, pollenate: 'full', competitor: 'partial', note: 'Limited to header image and color theme' },
    { name: 'QR code sharing', category: COLLECTION, pollenate: 'full', competitor: 'none' },
    { name: 'Real-time analytics dashboard', category: ANALYTICS, pollenate: 'full', competitor: 'partial', note: 'Basic summary charts only' },
    { name: 'AI-powered insights', category: ANALYTICS, pollenate: 'full', competitor: 'none' },
    { name: 'Trend analysis & NPS tracking', category: ANALYTICS, pollenate: 'full', competitor: 'none' },
    { name: 'Export to spreadsheet', category: ANALYTICS, pollenate: 'full', competitor: 'full', note: 'Google Sheets native integration' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'React / Web Component SDK', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Webhooks & automations', category: INTEGRATION, pollenate: 'full', competitor: 'partial', note: 'Only via Apps Script' },
    { name: 'Zapier integration', category: INTEGRATION, pollenate: 'full', competitor: 'partial' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Team collaboration', category: COLLABORATION, pollenate: 'full', competitor: 'full' },
  ],
  prosVsPollenate: [
    'Completely free with unlimited forms and responses',
    'Native, built-in integration with Google Workspace (Pollenate connects via Zapier)',
    'Familiar interface — most people already know how to use it',
    'No account signup needed for respondents',
  ],
  consVsPollenate: [
    'Embeddable feedback widgets you can drop into any page or app',
    'Dedicated NPS, CSAT, emoji, thumbs, and star rating question types',
    'Rich analytics with trend tracking, AI insights, and interactive dashboards',
    'Full REST API, React SDK, webhooks, and MCP server for developers',
    'Complete custom branding — your logo, colors, fonts, and domain',
    'Team feedback management with inboxes, notes, @mentions, and follow-ups',
    'QR code generation and shareable short URLs for feedback pages',
  ],
  switchReasons: [
    'Collect feedback anywhere with drop-in widgets — not just full-page forms',
    'Purpose-built NPS, CSAT, emoji, and star rating question types',
    'Real-time analytics with AI-powered insights and trend tracking',
    'Full REST API, React SDK, and MCP server for AI agent integration',
    'Custom branding — your logo, colors, fonts, and domain',
    'Still free to start — 5,000 events/month on the Starter plan',
  ],
  faqs: [
    { question: 'Is Pollenate free like Google Forms?', answer: 'Pollenate has a generous free Starter plan with 5,000 events/month, 1 inbox, and all widget types included. Google Forms is also free but lacks feedback widgets, analytics, and APIs.' },
    { question: 'Can Pollenate replace Google Forms for surveys?', answer: 'Yes. Pollenate supports multi-question feedback pages with 7+ question types including NPS, CSAT, stars, emoji, thumbs, and open text. You can share them via link, QR code, or embed them in your site.' },
    { question: 'Does Pollenate integrate with Google Sheets?', answer: 'Pollenate integrates with 7,000+ apps through Zapier, including Google Sheets. You can also export data via CSV or use our REST API for custom integrations.' },
    { question: 'Why would I use Pollenate over Google Forms?', answer: 'If you need embeddable feedback widgets, real-time analytics, developer APIs, custom branding, or AI-powered insights, Pollenate is purpose-built for this. Google Forms is a general-purpose form tool that works well for simple internal surveys.' },
  ],
  metaTitle: 'Pollenate vs Google Forms (2026) — Beyond Basic Surveys',
  metaDescription: 'Compare Pollenate and Google Forms for feedback collection. See why teams upgrade from Google Forms to Pollenate for widgets, analytics, APIs, and custom branding.',
};

const typeform: CompetitorData = {
  slug: 'typeform',
  name: 'Typeform',
  tagline: 'Beautiful interactive forms with expensive per-response pricing.',
  description: 'Typeform is known for its sleek one-question-at-a-time forms. However, it charges per response, making it expensive at scale. Pollenate offers similar interactive feedback collection at a fraction of the cost with developer-first tools.',
  logoEmoji: '✨',
  category: 'Interactive Form Builder',
  website: 'typeform.com',
  pricingUrl: 'https://www.typeform.com/pricing/',
  yearFounded: 2012,
  targetAudience: 'Marketers, product teams, and businesses collecting form data',
  pricing: {
    model: 'Per-response tiered pricing',
    hasFreeTier: false,
    plans: [
      {
        name: 'Basic',
        price: '$28',
        period: '/month',
        features: ['100 responses/month', '1 user', 'Unlimited forms', 'Basic integrations'],
      },
      {
        name: 'Plus',
        price: '$56',
        period: '/month',
        features: ['1,000 responses/month', '3 users', 'Remove branding', 'Custom subdomain'],
      },
      {
        name: 'Business',
        price: '$91',
        period: '/month',
        features: ['10,000 responses/month', '5 users', 'Drop-off analysis', 'Conversion tracking', 'Priority support'],
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        features: ['Custom response limits', 'SSO', 'HIPAA available', 'VIP support', 'Custom data center'],
      },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'partial', note: 'Typeform has embeds, but not lightweight widgets' },
    { name: 'One-at-a-time question flow', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'NPS / CSAT surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Emoji & thumbs reactions', category: COLLECTION, pollenate: 'full', competitor: 'none' },
    { name: 'Conditional logic / branching', category: COLLECTION, pollenate: 'none', competitor: 'full' },
    { name: 'Custom branding', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'QR code sharing', category: COLLECTION, pollenate: 'full', competitor: 'none' },
    { name: 'Real-time analytics', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'AI-powered insights', category: ANALYTICS, pollenate: 'full', competitor: 'partial', note: 'Typeform has AI form building, limited AI analytics' },
    { name: 'Drop-off analysis', category: ANALYTICS, pollenate: 'none', competitor: 'full' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'React / Web Component SDK', category: DEVELOPER, pollenate: 'full', competitor: 'partial', note: 'Typeform has embed SDK but not a component library' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Single script tag embed', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Zapier integration', category: INTEGRATION, pollenate: 'full', competitor: 'full' },
    { name: 'HubSpot / Mailchimp', category: INTEGRATION, pollenate: 'partial', competitor: 'full' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'none', note: 'Typeform has no free plan (Basic starts at $28/mo)' },
    { name: 'Unlimited responses on paid plans', category: PRICING_CAT, pollenate: 'full', competitor: 'none', note: 'Typeform caps responses per plan' },
  ],
  prosVsPollenate: [
    'Industry-leading interactive one-at-a-time form UX',
    'Advanced conditional logic and branching',
    'Drop-off analysis and conversion tracking on Business plan',
    'Extensive template library and AI-powered form building',
  ],
  consVsPollenate: [
    '50x more responses free — 5,000 events/month included on the Starter plan',
    'Lightweight embeddable feedback widgets for in-app collection',
    'Generous free tier with all widget types and API access',
    'Full developer toolkit: MCP server, single-script embed, React SDK',
    'Emoji, thumbs, and other reaction question types built in',
    'AI-powered analytics that summarize trends and recommend actions',
    'No per-response limits on events — predictable pricing at any scale',
  ],
  switchReasons: [
    'Get 50x more responses for free — 5,000 events/month vs. Typeform\'s 100 on Basic',
    'Drop-in feedback widgets that work anywhere with one line of code',
    'No per-response limits — Pollenate Team plan includes 100,000 events for $49/month',
    'Developer-first with REST API, React SDK, and MCP server',
    'AI-powered analytics that summarize trends and recommend actions',
    'All widget types included on every plan — NPS, CSAT, emoji, stars, thumbs, text',
  ],
  faqs: [
    { question: 'How does Pollenate pricing compare to Typeform?', answer: 'Pollenate\'s free tier includes 5,000 events/month — 50 times more than Typeform\'s cheapest plan ($28/month for 100 responses). Pollenate Team at $49/month includes 100,000 events, while Typeform Business at $91/month only includes 10,000 responses.' },
    { question: 'Does Pollenate support one-at-a-time question flow like Typeform?', answer: 'Yes. Pollenate feedback pages can display questions one at a time with a progress bar and Next/Previous navigation, just like Typeform\'s signature conversational flow.' },
    { question: 'Can I embed Pollenate forms like Typeform?', answer: 'Pollenate goes beyond Typeform\'s embed options. You can embed lightweight feedback widgets with a single script tag, use the React SDK for deep integration, or share feedback pages via link, QR code, or iframe.' },
    { question: 'Does Typeform have a free plan?', answer: 'Typeform no longer offers a free plan. Their cheapest option (Basic) starts at $28/month for 100 responses. Pollenate offers a free Starter plan with 5,000 events/month, all widget types, and API access.' },
  ],
  metaTitle: 'Pollenate vs Typeform (2026) — 50x More Responses, Lower Cost',
  metaDescription: 'Compare Pollenate and Typeform pricing and features. Pollenate offers 5,000 free responses vs. Typeform\'s 100 for $28/mo. See the full comparison.',
};

const surveyMonkey: CompetitorData = {
  slug: 'surveymonkey',
  name: 'SurveyMonkey',
  tagline: 'Legacy survey platform with expensive per-user pricing.',
  description: 'SurveyMonkey is one of the oldest online survey tools. While feature-rich for traditional surveys, its per-user pricing model and lack of embeddable widgets make it a costly choice for modern product feedback.',
  logoEmoji: '🐒',
  category: 'Online Survey Platform',
  website: 'surveymonkey.com',
  pricingUrl: 'https://www.surveymonkey.com/pricing/',
  yearFounded: 1999,
  targetAudience: 'Businesses, researchers, and HR teams',
  pricing: {
    model: 'Per-user pricing (minimum 3 users for team plans)',
    hasFreeTier: true,
    plans: [
      {
        name: 'Free',
        price: '$0',
        features: ['10 questions per survey', '40 responses per survey', 'Limited features'],
      },
      {
        name: 'Team Advantage',
        price: '$30',
        period: '/user/month',
        features: ['50,000 responses/year', 'Unlimited surveys', 'Skip logic', 'Collaboration tools', 'Min. 3 users ($90/mo minimum)'],
      },
      {
        name: 'Team Premier',
        price: '$92',
        period: '/user/month',
        features: ['100,000 responses/year', 'White label', 'Multilingual', 'Sentiment analysis', 'Crosstabs', 'Min. 3 users ($276/mo minimum)'],
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        features: ['Custom response limits', 'SSO', 'HIPAA available', 'US/Canada/EU data center', 'Enhanced governance'],
      },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'partial', note: 'SurveyMonkey has web link embeds, not lightweight widgets' },
    { name: 'Multi-question surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'NPS / CSAT surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Emoji & thumbs reactions', category: COLLECTION, pollenate: 'full', competitor: 'none' },
    { name: 'Skip logic & branching', category: COLLECTION, pollenate: 'none', competitor: 'full' },
    { name: 'Template library', category: COLLECTION, pollenate: 'partial', competitor: 'full' },
    { name: 'QR code sharing', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Real-time analytics', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'AI-powered insights', category: ANALYTICS, pollenate: 'full', competitor: 'partial', note: 'SurveyMonkey has AI build, limited AI analysis' },
    { name: 'Sentiment analysis', category: ANALYTICS, pollenate: 'partial', competitor: 'full' },
    { name: 'Crosstab analysis', category: ANALYTICS, pollenate: 'none', competitor: 'full' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'React / Web Component SDK', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Zapier integration', category: INTEGRATION, pollenate: 'full', competitor: 'full' },
    { name: 'Salesforce / HubSpot', category: INTEGRATION, pollenate: 'partial', competitor: 'full' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'partial', note: 'SurveyMonkey free: 10 questions, 40 responses per survey' },
    { name: 'No per-user pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'none', note: 'SurveyMonkey charges per user with 3-user minimum' },
  ],
  prosVsPollenate: [
    'Extensive survey template library with hundreds of pre-built surveys',
    'Built-in audience panel (SurveyMonkey Audience) for recruiting respondents',
    'Multilingual survey support on Premier plan',
    'Established brand trusted by millions since 1999',
  ],
  consVsPollenate: [
    'Per-organization pricing with unlimited team members — no per-seat costs',
    'Generous free tier: 5,000 events/month with no per-survey limits',
    'Embeddable feedback widgets you can drop into any page with one script tag',
    'Full developer toolkit: React SDK, MCP server, and single-script embed',
    'Monthly event limits that are easy to predict and budget for',
    'All feedback types included: emoji, thumbs, stars, NPS, CSAT, and text',
    'Modern, fast UI built on edge infrastructure with sub-50ms response times',
  ],
  switchReasons: [
    'Per-organization pricing with unlimited team members — no per-seat costs',
    'Generous free tier: 5,000 events/month vs. SurveyMonkey\'s 40 responses per survey',
    'Drop-in feedback widgets that work anywhere with one script tag',
    'Developer-first with REST API, React SDK, and MCP server for AI agents',
    'Modern, fast UI built on edge infrastructure with sub-50ms response times',
    'AI-powered insights that automatically summarize feedback trends',
  ],
  faqs: [
    { question: 'How does Pollenate pricing compare to SurveyMonkey?', answer: 'SurveyMonkey charges per user with a 3-user minimum. Team Advantage costs $30/user/month ($90/month minimum) for 50,000 responses per year. Pollenate charges per organization: $0 for 5,000 events/month or $49/month for 100,000 events with unlimited team members.' },
    { question: 'Can Pollenate replace SurveyMonkey for surveys?', answer: 'For feedback collection, product surveys, and user satisfaction tracking, yes. Pollenate supports multi-question feedback pages with NPS, CSAT, ratings, and text questions. For academic research or complex branching logic, SurveyMonkey may still have an edge.' },
    { question: 'Does Pollenate have survey templates?', answer: 'Pollenate offers AI-powered form generation — describe what you need and the AI builds your survey. You can also use the drag-and-drop editor to create forms from scratch with 7+ question types.' },
    { question: 'Is SurveyMonkey really free?', answer: 'SurveyMonkey\'s free plan limits you to 10 questions per survey and 40 responses per survey — often not enough for any real use case. Pollenate\'s free tier includes 5,000 events/month with no per-survey limits.' },
  ],
  metaTitle: 'Pollenate vs SurveyMonkey (2026) — No Per-User Pricing',
  metaDescription: 'Compare Pollenate and SurveyMonkey pricing and features. Pollenate offers unlimited users and 5,000 free events/mo. See why teams are switching.',
};

const hotjar: CompetitorData = {
  slug: 'hotjar',
  name: 'Hotjar',
  tagline: 'Website analytics tool where feedback is a secondary feature.',
  description: 'Hotjar (now part of Contentsquare) focuses on heatmaps, session recordings, and behavior analytics. While it includes surveys and feedback widgets, these are secondary features. Pollenate is purpose-built for structured feedback collection.',
  logoEmoji: '🔥',
  category: 'Website Behavior Analytics',
  website: 'hotjar.com',
  pricingUrl: 'https://www.hotjar.com/pricing/',
  yearFounded: 2014,
  targetAudience: 'UX designers, product managers, and marketers',
  pricing: {
    model: 'Per-product pricing (Observe, Ask, Engage sold separately)',
    hasFreeTier: true,
    plans: [
      {
        name: 'Basic (Free)',
        price: '$0',
        features: ['35 daily sessions', '3 heatmaps', '3 surveys', 'Limited recordings'],
      },
      {
        name: 'Plus',
        price: '$40',
        period: '/month',
        features: ['100 daily sessions', 'Unlimited heatmaps', 'Unlimited surveys', 'Session filters'],
      },
      {
        name: 'Business',
        price: '$99',
        period: '/month',
        features: ['500 daily sessions', 'Identify API', 'Custom integrations', 'Priority support'],
        highlighted: true,
      },
      {
        name: 'Scale',
        price: '$213',
        period: '/month',
        features: ['Unlimited sessions', 'Advanced filters', 'Funnels', 'Trends', 'Console tracking'],
      },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Multi-question surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'NPS / CSAT surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Emoji & thumbs reactions', category: COLLECTION, pollenate: 'full', competitor: 'partial' },
    { name: 'QR code sharing', category: COLLECTION, pollenate: 'full', competitor: 'none' },
    { name: 'Custom branding', category: COLLECTION, pollenate: 'full', competitor: 'partial' },
    { name: 'Heatmaps', category: ANALYTICS, pollenate: 'none', competitor: 'full' },
    { name: 'Session recordings', category: ANALYTICS, pollenate: 'none', competitor: 'full' },
    { name: 'Real-time feedback analytics', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'AI-powered insights', category: ANALYTICS, pollenate: 'full', competitor: 'partial' },
    { name: 'Funnel analysis', category: ANALYTICS, pollenate: 'none', competitor: 'full' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'partial' },
    { name: 'React / Web Component SDK', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Zapier integration', category: INTEGRATION, pollenate: 'full', competitor: 'full' },
    { name: 'Slack / Teams notifications', category: INTEGRATION, pollenate: 'full', competitor: 'full' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Feedback-first design', category: PRICING_CAT, pollenate: 'full', competitor: 'none', note: 'Hotjar\'s feedback is secondary to behavior analytics' },
  ],
  prosVsPollenate: [
    'Heatmaps and session recordings provide visual behavior analytics',
    'Funnel analysis helps identify drop-off points in user flows',
    'Established user base with 1.3 million+ websites',
    'Combined behavior + feedback data in one tool',
  ],
  consVsPollenate: [
    'Purpose-built for feedback — collecting and acting on it is the core product',
    'Simple per-organization pricing instead of paying per product or session',
    'Full developer toolkit: REST API, React SDK, and MCP server',
    'Shareable feedback pages with custom branding and QR codes',
    'Team inbox management with notes, @mentions, tags, and follow-ups',
    'Deeply customizable feedback widgets with full branding control',
    'Independent product with a predictable, transparent roadmap',
  ],
  switchReasons: [
    'Purpose-built for feedback collection — not a secondary feature',
    'Full REST API, React SDK, and MCP server for developer workflows',
    'Shareable feedback pages with QR codes and custom branding',
    'Team inbox with notes, @mentions, tags, and follow-up workflows',
    'AI-powered insights that analyze feedback trends and sentiment',
    'Transparent per-organization pricing — not per-product or per-session',
  ],
  faqs: [
    { question: 'Can Pollenate replace Hotjar for feedback collection?', answer: 'If your primary need is collecting and managing user feedback, yes. Pollenate is purpose-built for feedback with 7+ widget types, feedback pages, team inboxes, and AI analytics. If you also need heatmaps and session recordings, you might use Pollenate alongside a behavior analytics tool.' },
    { question: 'Does Pollenate have heatmaps or session recordings?', answer: 'No. Pollenate is focused on structured feedback collection, analysis, and action. For heatmaps and recordings, tools like Hotjar or FullStory are purpose-built. Many teams use both — Pollenate for feedback and a separate tool for behavior analytics.' },
    { question: 'How does Pollenate pricing compare to Hotjar?', answer: 'Hotjar charges separately for Observe (heatmaps), Ask (surveys), and Engage (interviews). The Ask product alone starts at $40/month. Pollenate includes all feedback features on every plan, starting free with 5,000 events/month.' },
    { question: 'Is Hotjar still independent?', answer: 'Hotjar was acquired by Contentsquare in 2021. The Hotjar brand continues to exist, but pricing and features are now managed under the Contentsquare umbrella, which may lead to changes.' },
  ],
  metaTitle: 'Pollenate vs Hotjar (2026) — Purpose-Built Feedback Collection',
  metaDescription: 'Compare Pollenate and Hotjar for user feedback. See why teams choose Pollenate\'s dedicated feedback platform over Hotjar\'s secondary survey features.',
};

const canny: CompetitorData = {
  slug: 'canny',
  name: 'Canny',
  tagline: 'Feature voting board with per-tracked-user pricing.',
  description: 'Canny is a product feedback management tool focused on feature requests, voting boards, and public roadmaps. Pollenate takes a different approach with embeddable widgets, multi-format feedback collection, and developer-first integrations.',
  logoEmoji: '📋',
  category: 'Product Feedback Management',
  website: 'canny.io',
  pricingUrl: 'https://canny.io/pricing',
  yearFounded: 2017,
  targetAudience: 'Product managers and SaaS teams',
  pricing: {
    model: 'Per-tracked-user pricing with usage-based scaling',
    hasFreeTier: true,
    plans: [
      {
        name: 'Free',
        price: '$0',
        features: ['25 tracked users', 'Unlimited posts', 'Automatic feedback capture', '1 roadmap'],
      },
      {
        name: 'Core',
        price: '$19',
        period: '/month',
        features: ['100+ tracked users', 'Custom domains', 'Content translations', 'API & webhooks'],
      },
      {
        name: 'Pro',
        price: '$79',
        period: '/month',
        features: ['100+ tracked users', 'PM integrations (Jira, Linear)', 'Advanced privacy', 'Custom admin roles', 'Unlimited roadmaps'],
        highlighted: true,
      },
      {
        name: 'Business',
        price: 'Custom',
        features: ['5,000+ tracked users', 'SSO (Okta, Entra ID)', 'CRM (HubSpot, Salesforce)', 'SOC 2 compliance', 'Pay by invoice'],
      },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'partial', note: 'Canny has a widget, but limited to voting boards' },
    { name: 'Multi-question surveys', category: COLLECTION, pollenate: 'full', competitor: 'none', note: 'Canny collects feature requests, not surveys' },
    { name: 'NPS / CSAT / Emoji / Stars', category: COLLECTION, pollenate: 'full', competitor: 'none' },
    { name: 'Feature voting boards', category: COLLECTION, pollenate: 'none', competitor: 'full' },
    { name: 'Public roadmap', category: COLLECTION, pollenate: 'none', competitor: 'full' },
    { name: 'Product changelog', category: COLLECTION, pollenate: 'partial', competitor: 'full', note: 'Pollenate has a changelog page, but not a product changelog tool' },
    { name: 'QR code sharing', category: COLLECTION, pollenate: 'full', competitor: 'none' },
    { name: 'Real-time analytics', category: ANALYTICS, pollenate: 'full', competitor: 'partial' },
    { name: 'AI-powered insights', category: ANALYTICS, pollenate: 'full', competitor: 'full', note: 'Canny has Autopilot AI for feedback discovery and summaries' },
    { name: 'User segmentation', category: ANALYTICS, pollenate: 'partial', competitor: 'full' },
    { name: 'MRR impact tracking', category: ANALYTICS, pollenate: 'none', competitor: 'full' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'React / Web Component SDK', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'full', note: 'Both offer MCP connectors' },
    { name: 'Jira / Linear / Asana', category: INTEGRATION, pollenate: 'partial', competitor: 'full', note: 'Pollenate connects via Zapier; Canny has native integrations' },
    { name: 'Zapier integration', category: INTEGRATION, pollenate: 'full', competitor: 'full' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Predictable pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'partial', note: 'Canny auto-upgrades when you exceed tracked user limits' },
  ],
  prosVsPollenate: [
    'Purpose-built feature voting and prioritization system',
    'Public roadmaps that keep customers informed about planned features',
    'AI-powered Autopilot for automatic feedback discovery from support tools',
    'Native PM integrations with Jira, Linear, Asana, and GitHub (Pollenate connects via Zapier)',
    'MRR impact tracking ties feature requests to revenue',
  ],
  consVsPollenate: [
    'Predictable per-organization pricing — no surprise auto-upgrades based on users',
    'Multi-question surveys and feedback forms with 7+ question types',
    'All feedback types built in: NPS, CSAT, emoji, stars, thumbs, and text',
    'Embeddable rating widgets for real-time in-app feedback collection',
    'Shareable feedback pages with custom branding, QR codes, and multiple question types',
    'Full developer SDK with React components and single-script embed',
    'Free tier with 5,000 events/month — not limited by tracked users',
  ],
  switchReasons: [
    'Collect structured feedback with 7+ question types — not just feature requests',
    'Embeddable widgets for real-time in-app feedback with one script tag',
    'Predictable per-org pricing — no surprise auto-upgrades based on tracked users',
    'Developer-first with REST API, React SDK, and single-script embed',
    'Shareable feedback pages with custom branding and QR codes',
    'AI-powered insights that analyze trends across all feedback types',
  ],
  faqs: [
    { question: 'Is Pollenate a replacement for Canny?', answer: 'Pollenate and Canny solve different aspects of the feedback problem. Canny focuses on feature voting, prioritization, and public roadmaps. Pollenate focuses on structured feedback collection via embeddable widgets, surveys, and analytics. Some teams use both, while others find Pollenate sufficient for their feedback needs.' },
    { question: 'Does Pollenate have feature voting or roadmaps?', answer: 'Pollenate doesn\'t include feature voting or public roadmaps — it focuses on collecting structured feedback (ratings, NPS, CSAT, surveys) and providing AI-powered analytics. If you need voting boards, Canny or similar tools complement Pollenate well.' },
    { question: 'How does pricing compare?', answer: 'Canny\'s pricing scales with tracked users and auto-upgrades when you exceed limits. Pollenate charges a flat per-org fee: free for 5,000 events/month, $49/month for 100,000 events, with unlimited team members on every plan.' },
    { question: 'Can I use Pollenate and Canny together?', answer: 'Absolutely. Many teams use Pollenate for in-app feedback collection and real-time analytics, while using Canny for feature request management and public roadmaps. Both integrate with Zapier for workflow automation.' },
  ],
  metaTitle: 'Pollenate vs Canny (2026) — Structured Feedback vs Feature Voting',
  metaDescription: 'Compare Pollenate and Canny for product feedback. See how Pollenate\'s embeddable widgets, surveys, and AI analytics differ from Canny\'s feature voting boards.',
};

const delighted: CompetitorData = {
  slug: 'delighted',
  name: 'Delighted',
  tagline: 'Simple standalone NPS and survey tool with response-metered pricing and no embeddable widgets.',
  description: 'Delighted (by Qualtrics) is a focused experience-survey tool known for clean NPS, CSAT, and CES surveys over email, link, and web. It is easy to use but limited beyond surveys, with response-metered plans and no developer-grade embedding.',
  logoEmoji: '😊',
  category: 'NPS & Survey Tool',
  website: 'delighted.com',
  pricingUrl: 'https://delighted.com/pricing',
  yearFounded: 2013,
  targetAudience: 'Teams that want simple, standalone NPS/CSAT surveys',
  pricing: {
    model: 'Per-response tiers, billed monthly or annually',
    hasFreeTier: true,
    plans: [
      { name: 'Free', price: '$0', features: ['Limited monthly responses', 'NPS or one survey type', 'Basic reporting'] },
      { name: 'Premium', price: '$224', period: '/month', features: ['More responses', 'Multiple survey types', 'Integrations'] },
      { name: 'Premium Plus', price: 'Custom', features: ['Higher volume', 'Advanced reporting', 'Priority support'] },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'partial', note: 'Delighted offers web surveys, not one-tap on-page widgets' },
    { name: 'Multi-question feedback pages', category: COLLECTION, pollenate: 'full', competitor: 'partial' },
    { name: 'NPS / CSAT / CES surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Emoji & thumbs reactions', category: COLLECTION, pollenate: 'full', competitor: 'partial' },
    { name: 'Real-time analytics dashboard', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'AI semantic search', category: ANALYTICS, pollenate: 'full', competitor: 'none' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'React / Web Component SDK', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Single script tag embed', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'partial' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'No per-response metering', category: PRICING_CAT, pollenate: 'full', competitor: 'none', note: 'Pollenate plans include generous event allowances, not strict per-response caps' },
    { name: 'HIPAA BAA available', category: PRICING_CAT, pollenate: 'full', competitor: 'partial' },
  ],
  prosVsPollenate: [
    'Very simple, opinionated setup for basic NPS',
    'Part of the Qualtrics family with established survey heritage',
    'Clean email and link survey delivery',
  ],
  consVsPollenate: [
    'Embeddable one-tap widgets with a single script tag',
    'Developer toolkit: REST API, React SDK, and MCP server',
    'AI semantic search across all feedback',
    'Per-organization pricing instead of per-response metering',
    'Free tier that scales into a full feedback platform',
  ],
  switchReasons: [
    'Embed feedback anywhere with one script tag',
    'Grow beyond NPS into widgets, feedback pages, and AI analysis',
    'Predictable per-organization pricing with unlimited seats',
    'Developer-first APIs, SDKs, and an MCP server',
    'HIPAA-ready architecture with a BAA option',
  ],
  faqs: [
    { question: 'Is Pollenate a good Delighted alternative?', answer: 'Yes. Pollenate covers NPS, CSAT, and CES like Delighted, but adds embeddable one-tap widgets, a developer API and SDKs, AI semantic search, and per-organization pricing instead of per-response metering.' },
    { question: 'Does Pollenate have a free plan like Delighted?', answer: 'Yes. Pollenate has a free-forever Starter plan with 5,000 events per month, all widget types, and API access.' },
    { question: 'Can Pollenate embed surveys on a page?', answer: 'Yes. Pollenate widgets embed with a single script tag, and Feedback Pages can be shared by link, QR code, or iframe.' },
  ],
  metaTitle: 'Pollenate vs Delighted (2026) — Pricing, Features & Comparison',
  metaDescription: 'Compare Pollenate and Delighted for NPS and feedback. See pricing, embeddable widgets, developer APIs, and why teams choose Pollenate over Delighted.',
};

const survicate: CompetitorData = {
  slug: 'survicate',
  name: 'Survicate',
  tagline: 'Multi-channel survey tool with many integrations but strict response limits on lower tiers.',
  description: 'Survicate runs NPS, CSAT, and CES surveys across web, email, mobile, and in-product, with a large integration catalog. It is a solid mid-market survey tool, but response limits and feature gating push costs up as you scale.',
  logoEmoji: '📊',
  category: 'Multi-channel Survey Tool',
  website: 'survicate.com',
  pricingUrl: 'https://survicate.com/pricing/',
  yearFounded: 2013,
  targetAudience: 'Marketing and CX teams running multi-channel surveys',
  pricing: {
    model: 'Per-response tiers with feature gating',
    hasFreeTier: true,
    plans: [
      { name: 'Free', price: '$0', features: ['Limited responses/month', 'Core survey types', 'Basic integrations'] },
      { name: 'Business', price: '$99', period: '/month', features: ['More responses', 'Advanced targeting', 'Integrations'] },
      { name: 'Scale', price: 'Custom', features: ['High volume', 'Advanced features', 'Priority support'] },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Multi-question feedback pages', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'NPS / CSAT / CES surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Emoji & thumbs reactions', category: COLLECTION, pollenate: 'full', competitor: 'partial' },
    { name: 'Real-time analytics dashboard', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'AI semantic search', category: ANALYTICS, pollenate: 'full', competitor: 'partial' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'React / Web Component SDK', category: DEVELOPER, pollenate: 'full', competitor: 'partial' },
    { name: 'Single script tag embed', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'partial' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'No per-response metering', category: PRICING_CAT, pollenate: 'full', competitor: 'none' },
    { name: 'HIPAA BAA available', category: PRICING_CAT, pollenate: 'full', competitor: 'none' },
  ],
  prosVsPollenate: [
    'Large catalog of native integrations',
    'Mature multi-channel survey targeting',
    'Established mid-market presence',
  ],
  consVsPollenate: [
    'Per-organization pricing without strict response caps',
    'Developer toolkit including an MCP server for AI agents',
    'HIPAA-ready architecture with a BAA option',
    'Free tier that includes full API access',
    'AI semantic search across all feedback',
  ],
  switchReasons: [
    'Avoid response-metered pricing as you scale',
    'Get a BAA and HIPAA-ready architecture',
    'Use a developer-first API, SDK, and MCP server',
    'Search feedback with AI semantic search',
    'Invite unlimited team members on every plan',
  ],
  faqs: [
    { question: 'Is Pollenate a good Survicate alternative?', answer: 'Yes. Pollenate matches Survicate on multi-channel surveys and embeddable widgets, and adds per-organization pricing, a BAA option, an MCP server, and AI semantic search.' },
    { question: 'Does Pollenate limit responses like Survicate?', answer: 'Pollenate plans include generous monthly event allowances rather than strict per-response caps, so costs stay predictable as you grow.' },
    { question: 'Can I run NPS and CSAT in-product with Pollenate?', answer: 'Yes. Pollenate supports NPS, CSAT, CES, emoji, thumbs, stars, and text widgets you can embed in-product with one script tag.' },
  ],
  metaTitle: 'Pollenate vs Survicate (2026) — Pricing, Features & Comparison',
  metaDescription: 'Compare Pollenate and Survicate for surveys and feedback. See pricing, response limits, embeddable widgets, and why teams choose Pollenate over Survicate.',
};

const formbricks: CompetitorData = {
  slug: 'formbricks',
  name: 'Formbricks',
  tagline: 'Open-source survey platform you self-host — powerful and private, but you run the infrastructure.',
  description: 'Formbricks is an open-source experience management and survey platform you can self-host for full data ownership. It is a great fit for teams that need to control the stack, at the cost of hosting, scaling, and maintaining it yourself.',
  logoEmoji: '🧱',
  category: 'Open-source Survey Platform',
  website: 'formbricks.com',
  pricingUrl: 'https://formbricks.com/pricing',
  yearFounded: 2022,
  targetAudience: 'Developers and teams who want to self-host for data control',
  pricing: {
    model: 'Open-source (self-hosted) or managed cloud tiers',
    hasFreeTier: true,
    plans: [
      { name: 'Self-hosted', price: '$0', features: ['Open-source', 'Unlimited surveys', 'You host & maintain'] },
      { name: 'Cloud Free', price: '$0', features: ['Limited responses', 'Hosted for you', 'Core features'] },
      { name: 'Cloud Paid', price: 'From $49', period: '/month', features: ['More responses', 'Advanced features', 'Support'] },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Multi-question feedback pages', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'NPS / CSAT / CES surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Real-time analytics dashboard', category: ANALYTICS, pollenate: 'full', competitor: 'partial' },
    { name: 'AI semantic search', category: ANALYTICS, pollenate: 'full', competitor: 'none' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Single script tag embed', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'Fully managed hosting', category: DEVELOPER, pollenate: 'full', competitor: 'partial', note: 'Formbricks self-hosting requires you to run the infrastructure' },
    { name: 'Self-hosting option', category: DEVELOPER, pollenate: 'none', competitor: 'full', note: 'Formbricks is open-source and self-hostable; Pollenate is managed SaaS' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'HIPAA BAA available', category: PRICING_CAT, pollenate: 'full', competitor: 'partial' },
  ],
  prosVsPollenate: [
    'Fully open-source and self-hostable for total data control',
    'No vendor lock-in if you host it yourself',
    'Customizable at the source-code level',
  ],
  consVsPollenate: [
    'Zero infrastructure to run — fully managed SaaS',
    'AI semantic search and an MCP server out of the box',
    'A signed BAA on Enterprise without self-hosting',
    'Global edge delivery with sub-50ms response times',
    'Set up in minutes with no servers to maintain',
  ],
  switchReasons: [
    'Skip the cost and effort of self-hosting',
    'Get AI semantic search and an MCP server built in',
    'Sign a BAA without running your own stack',
    'Launch in minutes on a global edge network',
    'Predictable per-organization pricing',
  ],
  faqs: [
    { question: 'Is Pollenate a good Formbricks alternative?', answer: 'Yes, if you prefer a fully managed platform over self-hosting. Pollenate gives you embeddable widgets, AI semantic search, an MCP server, and a BAA option without running any infrastructure.' },
    { question: 'Is Pollenate open-source like Formbricks?', answer: 'Pollenate is a managed SaaS rather than self-hosted open source. It trades self-hosting for zero-maintenance hosting, edge performance, and built-in AI features.' },
    { question: 'Does Pollenate offer a BAA?', answer: 'Yes. A signed BAA is available on Enterprise plans, with a HIPAA-ready architecture — no self-hosting required.' },
  ],
  metaTitle: 'Pollenate vs Formbricks (2026) — Open-source vs Managed',
  metaDescription: 'Compare Pollenate and Formbricks for feedback and surveys. Managed SaaS vs open-source self-hosting — see features, pricing, and which fits your team.',
};

const tally: CompetitorData = {
  slug: 'tally',
  name: 'Tally',
  tagline: 'Free, unlimited form builder with a Notion-like editor — great forms, but not a feedback platform.',
  description: 'Tally is a popular form builder offering unlimited forms and responses for free with a clean, document-style editor. It is excellent for forms, but it is not built for one-tap feedback widgets, NPS classification, or a feedback analytics dashboard.',
  logoEmoji: '📋',
  category: 'Free Form Builder',
  website: 'tally.so',
  pricingUrl: 'https://tally.so/pricing',
  yearFounded: 2020,
  targetAudience: 'Anyone who needs flexible, free forms',
  pricing: {
    model: 'Generous free plan; flat-fee Pro',
    hasFreeTier: true,
    plans: [
      { name: 'Free', price: '$0', features: ['Unlimited forms', 'Unlimited responses', 'Core question types'] },
      { name: 'Pro', price: '$29', period: '/month', features: ['Branding removal', 'Conditional logic', 'Integrations'] },
      { name: 'Business', price: '$89', period: '/month', features: ['Team features', 'Advanced controls', 'Priority support'] },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'partial', note: 'Tally embeds forms, not one-tap rating widgets' },
    { name: 'Multi-question feedback pages', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'NPS / CSAT / CES surveys', category: COLLECTION, pollenate: 'full', competitor: 'partial', note: 'Tally can build the questions but does not classify NPS' },
    { name: 'Emoji & thumbs reactions', category: COLLECTION, pollenate: 'full', competitor: 'partial' },
    { name: 'Real-time analytics dashboard', category: ANALYTICS, pollenate: 'full', competitor: 'partial' },
    { name: 'NPS auto-classification', category: ANALYTICS, pollenate: 'full', competitor: 'none' },
    { name: 'AI semantic search', category: ANALYTICS, pollenate: 'full', competitor: 'none' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'partial' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Single script tag embed', category: DEVELOPER, pollenate: 'full', competitor: 'partial' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'HIPAA BAA available', category: PRICING_CAT, pollenate: 'full', competitor: 'none' },
  ],
  prosVsPollenate: [
    'Unlimited forms and responses on a genuinely free plan',
    'Fast, Notion-like form editor',
    'Great for general-purpose forms and quizzes',
  ],
  consVsPollenate: [
    'Purpose-built feedback widgets (emoji, NPS, CSAT, thumbs)',
    'Automatic NPS/CSAT classification and a feedback dashboard',
    'AI semantic search across responses',
    'Developer API, SDK, and MCP server',
    'HIPAA-ready architecture with a BAA option',
  ],
  switchReasons: [
    'Collect one-tap feedback, not just form submissions',
    'Get automatic NPS and CSAT scoring',
    'Search feedback with AI semantic search',
    'Embed rating widgets with a single script tag',
    'Add a BAA for regulated feedback',
  ],
  faqs: [
    { question: 'Is Pollenate a good Tally alternative?', answer: 'Yes, if your goal is feedback rather than general forms. Pollenate adds one-tap rating widgets, automatic NPS/CSAT classification, a feedback dashboard, and AI semantic search that Tally does not offer.' },
    { question: 'Is Pollenate free like Tally?', answer: 'Pollenate has a free-forever plan with 5,000 events per month. Tally is best for unlimited free forms; Pollenate is best for feedback collection and analysis.' },
    { question: 'Can Pollenate build multi-question forms?', answer: 'Yes. Pollenate Feedback Pages are drag-and-drop, multi-question forms with 18 question types, shareable by link, QR code, or embed.' },
  ],
  metaTitle: 'Pollenate vs Tally (2026) — Feedback Platform vs Form Builder',
  metaDescription: 'Compare Pollenate and Tally. Tally is a great free form builder; Pollenate is a feedback platform with rating widgets, NPS scoring, and AI analysis. See the differences.',
};

const sprig: CompetitorData = {
  slug: 'sprig',
  name: 'Sprig',
  tagline: 'In-product research and surveys with AI analysis, aimed at product teams — with enterprise pricing.',
  description: 'Sprig focuses on in-product surveys, user research, and AI-driven analysis for product teams. It is strong for research workflows, but pricing skews enterprise and it is less focused on lightweight, embeddable feedback widgets and developer APIs.',
  logoEmoji: '🌱',
  category: 'Product Research & Surveys',
  website: 'sprig.com',
  pricingUrl: 'https://sprig.com/pricing',
  yearFounded: 2019,
  targetAudience: 'Product and UX research teams',
  pricing: {
    model: 'Event/user-based tiers, custom for scale',
    hasFreeTier: true,
    plans: [
      { name: 'Free', price: '$0', features: ['Limited responses', 'Core surveys', 'Basic analysis'] },
      { name: 'Starter', price: 'From $175', period: '/month', features: ['More responses', 'AI analysis', 'Integrations'] },
      { name: 'Enterprise', price: 'Custom', features: ['High volume', 'Advanced research', 'Dedicated support'] },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Multi-question feedback pages', category: COLLECTION, pollenate: 'full', competitor: 'partial' },
    { name: 'NPS / CSAT / CES surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'In-product survey targeting', category: COLLECTION, pollenate: 'partial', competitor: 'full', note: 'Sprig has advanced behavioral targeting' },
    { name: 'Real-time analytics dashboard', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'AI analysis', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'AI semantic search', category: ANALYTICS, pollenate: 'full', competitor: 'partial' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'Single script tag embed', category: DEVELOPER, pollenate: 'full', competitor: 'partial' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'none' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'HIPAA BAA available', category: PRICING_CAT, pollenate: 'full', competitor: 'partial' },
  ],
  prosVsPollenate: [
    'Advanced in-product behavioral targeting',
    'Deep user-research and study workflows',
    'Mature AI analysis for product teams',
  ],
  consVsPollenate: [
    'Transparent, public pricing with a low entry point',
    'Lightweight one-tap widgets with a single script tag',
    'Developer toolkit including an MCP server',
    'Per-organization pricing with unlimited seats',
    'HIPAA-ready architecture with a BAA option',
  ],
  switchReasons: [
    'Get transparent pricing without enterprise minimums',
    'Embed feedback anywhere with one script tag',
    'Use a developer-first API, SDK, and MCP server',
    'Collect feedback across web, docs, and AI agents',
    'Sign a BAA for regulated feedback',
  ],
  faqs: [
    { question: 'Is Pollenate a good Sprig alternative?', answer: 'Yes, especially for teams that want lightweight, embeddable feedback with transparent pricing. Pollenate offers widgets, feedback pages, AI semantic search, and an MCP server without enterprise minimums.' },
    { question: 'Does Pollenate do in-product surveys?', answer: 'Yes. Pollenate widgets embed in-product with a single script tag and support NPS, CSAT, CES, emoji, thumbs, stars, and text.' },
    { question: 'How does Pollenate pricing compare to Sprig?', answer: 'Pollenate has a free tier and a $49/month Team plan with transparent per-organization pricing, whereas Sprig skews toward higher, often custom, pricing.' },
  ],
  metaTitle: 'Pollenate vs Sprig (2026) — Pricing, Features & Comparison',
  metaDescription: 'Compare Pollenate and Sprig for in-product feedback and surveys. See pricing, embeddable widgets, developer APIs, and why teams choose Pollenate over Sprig.',
};

const refiner: CompetitorData = {
  slug: 'refiner',
  name: 'Refiner',
  tagline: 'In-product micro-surveys for SaaS and PLG teams, with response-metered pricing.',
  description: 'Refiner specializes in in-product micro-surveys for SaaS and product-led growth teams, with good targeting and integrations. It is focused on surveys, with response-based pricing and without the broader widget, AI-search, and developer surface Pollenate offers.',
  logoEmoji: '🔬',
  category: 'In-product Survey Tool',
  website: 'refiner.io',
  pricingUrl: 'https://refiner.io/pricing/',
  yearFounded: 2019,
  targetAudience: 'SaaS and product-led growth teams',
  pricing: {
    model: 'Per-response / per-user tiers',
    hasFreeTier: true,
    plans: [
      { name: 'Free', price: '$0', features: ['Limited responses', 'Core survey types', 'Basic integrations'] },
      { name: 'Essential', price: 'From $79', period: '/month', features: ['More responses', 'Targeting', 'Integrations'] },
      { name: 'Growth', price: 'Custom', features: ['Higher volume', 'Advanced features', 'Priority support'] },
    ],
  },
  features: [
    { name: 'Embeddable feedback widgets', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Multi-question feedback pages', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'NPS / CSAT / CES surveys', category: COLLECTION, pollenate: 'full', competitor: 'full' },
    { name: 'Emoji & thumbs reactions', category: COLLECTION, pollenate: 'full', competitor: 'partial' },
    { name: 'Real-time analytics dashboard', category: ANALYTICS, pollenate: 'full', competitor: 'full' },
    { name: 'AI semantic search', category: ANALYTICS, pollenate: 'full', competitor: 'none' },
    { name: 'REST API', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'MCP server for AI agents', category: DEVELOPER, pollenate: 'full', competitor: 'none' },
    { name: 'React / Web Component SDK', category: DEVELOPER, pollenate: 'full', competitor: 'partial' },
    { name: 'Single script tag embed', category: DEVELOPER, pollenate: 'full', competitor: 'full' },
    { name: 'Transparent pricing', category: PRICING_CAT, pollenate: 'full', competitor: 'partial' },
    { name: 'Free tier available', category: PRICING_CAT, pollenate: 'full', competitor: 'full' },
    { name: 'No per-response metering', category: PRICING_CAT, pollenate: 'full', competitor: 'none' },
    { name: 'HIPAA BAA available', category: PRICING_CAT, pollenate: 'full', competitor: 'none' },
  ],
  prosVsPollenate: [
    'Strong product-led growth survey targeting',
    'Good segmentation and trait-based triggering',
    'Focused integrations for SaaS stacks',
  ],
  consVsPollenate: [
    'Per-organization pricing without strict response caps',
    'AI semantic search across all feedback',
    'An MCP server and skill file for AI agents',
    'HIPAA-ready architecture with a BAA option',
    'Six widget types plus shareable Feedback Pages',
  ],
  switchReasons: [
    'Avoid response-metered pricing as you scale',
    'Search feedback with AI semantic search',
    'Collect feedback from AI agents via the API and MCP server',
    'Add a BAA for regulated feedback',
    'Invite unlimited team members on every plan',
  ],
  faqs: [
    { question: 'Is Pollenate a good Refiner alternative?', answer: 'Yes. Pollenate matches Refiner on in-product surveys and adds AI semantic search, an MCP server, a BAA option, and per-organization pricing instead of per-response metering.' },
    { question: 'Does Pollenate work for product-led growth?', answer: 'Yes. You can embed targeted NPS, CSAT, and feature-feedback widgets in-product with metadata segmentation by plan, cohort, or experiment.' },
    { question: 'Does Pollenate meter responses like Refiner?', answer: 'Pollenate includes generous monthly event allowances rather than strict per-response caps, keeping costs predictable.' },
  ],
  metaTitle: 'Pollenate vs Refiner (2026) — Pricing, Features & Comparison',
  metaDescription: 'Compare Pollenate and Refiner for in-product surveys and feedback. See pricing, response limits, AI search, and why teams choose Pollenate over Refiner.',
};

// --- Export all competitors ---

export const competitors: CompetitorData[] = [
  qualtrics,
  googleForms,
  typeform,
  surveyMonkey,
  hotjar,
  canny,
  delighted,
  survicate,
  formbricks,
  tally,
  sprig,
  refiner,
];

export function getCompetitorBySlug(slug: string): CompetitorData | undefined {
  return competitors.find((c) => c.slug === slug);
}
